import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
  // useContext,
} from "react";

import {
  Success,
  Edit,
  Delete,
  Level0Good,
  Level3Bad,
} from "@hitachivantara/uikit-react-icons";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IdType, SortingRule } from "react-table";
import { HvButton, HvGrid } from "@hitachivantara/uikit-react-core";
import {
  useHvTable,
  HvTableColumnConfig,
  useHvBulkActions,
  useHvPagination,
  useHvRowSelection,
  useHvSortBy,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";

import SimpleTable, {
  textColumn,
  // numberColumn,
  actionsColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  dateColumn,
} from "components/common/SimpleTable";

// eslint-disable-next-line import/no-cycle
import {
  CameraFilter,
  CameraFilters,
  useCameras,
  useEntityTypeCamera,
} from "lib/api";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import { NotificationContext } from "lib/context/NotificationContext";
import DeleteDialog from "components/common/DeleteDialog";
import { deleteEntities } from "lib/api/entity";
import { UserPolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import useStyles from "./styles";
import CameraTableControls from "../CameraTableControls";

interface CameraDataRow extends Record<string, unknown> {
  id: string;
  name: string;
  description: string;
  status: string;
  typeName: string;
  parentName: string;
  vmsName: string;
  gatewayName: string;
  ptz: boolean;
  audio: boolean;
  location: boolean;
  vmsId: string;
  entityTypeId: string;
  createDate: Date;
  updateDate: Date;
}

const sortFieldMap = {
  createDate: "createDate",
  updateDate: "updateDate",
  status: "status",
  name: "name",
  description: "description",
  typeName: "typeName",
  parentName: "parentName",
  vmsName: "vmsName",
  gatewayName: "gatewayName",
  audio: "audio",
  ptz: "ptz",
  location: "location",
};

const DEFAULT_SORT_CAMERA = [
  {
    id: "name",
    desc: false,
  },
];

const CameraTable: React.FC = () => {
  const { t } = useTranslation("camera");
  const [tableData, setTableData] = useState<CameraDataRow[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const { data: cameraType } = useEntityTypeCamera();

  const history = useHistory();

  const [filter, setFilter] = useState<CameraFilters[]>();
  const [selectedIndexFilter, setSelectedIndexFilter] = useState<number[][]>();
  const [baseFilter, setBaseFilter] = useState<CameraFilter[]>();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const classes = useStyles();
  const { hasSystemTypeNamePolicy } = useContext(UserDataContext);
  const hasDeleteRight = useMemo(() => {
    return hasSystemTypeNamePolicy("camera", UserPolicyTypes.delete);
  }, [hasSystemTypeNamePolicy]);
  const hasInsertUpdateRight = useMemo(() => {
    return hasSystemTypeNamePolicy("camera", UserPolicyTypes.insertupdate);
  }, [hasSystemTypeNamePolicy]);
  const columns = useMemo(
    (): HvTableColumnConfig<CameraDataRow>[] => [
      dateColumn({
        Header: t("columns.cameraUpdatedDate") as string,
        accessor: "updateDate",
        style: { width: "30%" },
      }),
      dateColumn({
        Header: t("columns.cameraCreatedDate") as string,
        accessor: "createDate",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("columns.cameraStatus") as string,
        accessor: "status",
        style: { width: "40%", textOverflow: "ellipsis", overflow: "hidden" },
        Cell: ({ row }: HvCellProps<CameraDataRow, boolean>) =>
          row.original.status === "CameraOnline" ? (
            <span>
              <Level0Good
                color={["#27ae60", "#fff"]}
                className={classes.statusIcon}
              />
              <span
                title={t("filter.cameraOnline")}
                className={classes.statusLabel}
              >
                {t("filter.cameraOnline")}
              </span>
            </span>
          ) : row.original.status === "CameraOffline" ? (
            <span>
              <Level3Bad
                color={["#c0392b", "#fff"]}
                className={classes.statusIcon}
              />
              <span
                title={t("filter.cameraOffline")}
                className={classes.statusLabel}
              >
                {t("filter.cameraOffline")}
              </span>
            </span>
          ) : row.original.status === "CameraNoLongerExist" ? (
            <span>
              <Level3Bad
                color={["#ffc602", "#fff"]}
                className={classes.statusIcon}
              />
              <span
                title={t("filter.cameraNoLongerExist")}
                className={classes.statusLabel}
              >
                {t("filter.cameraNoLongerExist")}
              </span>
            </span>
          ) : row.original.status === "StatusNotAvailable" ? (
            <span>
              <Level3Bad
                color={["#676a6c", "#fff"]}
                className={classes.statusIcon}
              />
              <span
                title={t("filter.statusNotAvailable")}
                className={classes.statusLabel}
              >
                {t("filter.statusNotAvailable")}
              </span>
            </span>
          ) : (
            row.original.status === "GatewayOffline" && (
              <span>
                <Level3Bad
                  color={["#aa00ff", "#fff"]}
                  className={classes.statusIcon}
                />
                <span
                  title={t("filter.gatewayOffline")}
                  className={classes.statusLabel}
                >
                  {t("filter.gatewayOffline")}
                </span>
              </span>
            )
          ),
      }),
      textColumn({
        Header: t("columns.cameraName") as string,
        accessor: "name",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("columns.description") as string,
        accessor: "description",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("columns.cameraTypeName") as string,
        accessor: "typeName",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("columns.cameraParentName") as string,
        accessor: "parentName",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("columns.cameraVmsName") as string,
        accessor: "vmsName",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("columns.cameraGatewayName") as string,
        accessor: "gatewayName",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("columns.cameraAudio") as string,
        accessor: "audio",
        style: { width: "20%" },
        Cell: ({ row }: HvCellProps<CameraDataRow, boolean>) =>
          row.original.audio ? (
            <Success color="green" className={classes.trueIcon} />
          ) : (
            <span className={classes.noIcon}>—</span>
          ),
      }),
      textColumn({
        Header: t("columns.cameraPtz") as string,
        accessor: "ptz",
        style: { width: "20%" },
        Cell: ({ row }: HvCellProps<CameraDataRow, boolean>) =>
          row.original.ptz ? (
            <Success color="green" className={classes.trueIcon} />
          ) : (
            <span className={classes.noIcon}>—</span>
          ),
      }),
      textColumn({
        Header: t("columns.cameraLocation") as string,
        accessor: "location",
        style: { width: "20%" },
        Cell: ({ row }: HvCellProps<CameraDataRow, boolean>) =>
          row.original.location ? (
            <Success color="green" className={classes.trueIcon} />
          ) : (
            <span className={classes.noIcon}>—</span>
          ),
      }),
      actionsColumn({
        id: "edit",
        Cell: ({ row }: HvCellProps<CameraDataRow>) => {
          return (
            <HvButton
              aria-label={t("labels.editCameraButtonLabel")}
              icon
              onClick={() =>
                history.push(`/settings/camera/edit/${row.original.id}`)
              }
            >
              <Edit />
            </HvButton>
          );
        },
      }),
    ],
    [
      t,
      classes.statusIcon,
      classes.statusLabel,
      classes.trueIcon,
      classes.noIcon,
      history,
    ]
  );

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] =
    useState<SortingRule<CameraDataRow>[]>(DEFAULT_SORT_CAMERA);
  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<CameraDataRow>, boolean>
  >({});

  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];

  const {
    data: camerasData,
    error: camerasError,
    mutate,
  } = useCameras(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: encodeURIComponent(sortField),
      Order: encodeURIComponent(sortBy[0].desc ? "desc" : "asc"),
      SearchKey: searchKey,
      calculateFilter: true,
    },
    { suspense: tableData == null },
    filter
  );

  useEffect(() => {
    if (camerasData && camerasData.data.filters.length > 0) {
      setBaseFilter(camerasData.data.filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (camerasData) {
      setTableData(
        camerasData.data.results.map((camera) => {
          return {
            id: camera.id,
            name: camera.name,
            description: camera.description,
            status: camera.status,
            typeName: camera.typeName,
            parentName: camera.parentName,
            vmsName: camera.vmsName,
            gatewayName: camera.gatewayName,
            ptz: camera.ptz,
            audio: camera.audio,
            location: camera.location,
            vmsId: camera.vmsId,
            entityTypeId: camera.entityTypeId,
            updateDate: camera.updateDate,
            createDate: camera.createDate,
          };
        })
      );
      setTotalCount(camerasData.data.totalCount);
    }
  }, [camerasData]);

  const initialState = useMemo(() => {
    const entries = tableData?.map<[string, boolean]>((v) => [
      v.id.toString(),
      !hasDeleteRight
        ? true
        : v.vmsId !== undefined && v.status !== "CameraNoLongerExist",
    ]);

    return {
      pageIndex: page,
      pageSize: size,
      sortBy,
      selectedRowIds: selectedIds,
      lockedSelectionRowIds: (entries && Object.fromEntries(entries)) ?? {},
    };
  }, [tableData, page, size, sortBy, selectedIds, hasDeleteRight]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectIncidentCheckbox"),
    }),
    [t]
  );

  const bulkActions = useMemo(
    () => [
      {
        id: "delete",
        label: t("labels.bulkDeleteCameraButton"),
        icon: <Delete />,
        onClick: () => {
          setIsDeleteDialogOpen(true);
        },
      },
    ],
    [t]
  );

  const onDeleteClose = useCallback(
    (result, message, ids) => {
      setIsDeleteDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  const instance = useHvTable<CameraDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: CameraDataRow): string => `${v.id}`, []),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { selectedRowIds, pageIndex, pageSize, sortBy: sortByFromTable },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  const onSearchHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onFilterHandler = (value: number[][]) => {
    const filters: CameraFilters[] = [];
    if (baseFilter) {
      baseFilter.map((f, i) => {
        const selectedValue = value[i] || [];
        const arrFilters: string[] = [];
        for (let index = 0; index < selectedValue.length; index += 1) {
          arrFilters.push(f.values[selectedValue[index]].value);
        }
        return filters.push({ name: f.name, values: arrFilters });
      });
    }
    setFilter(filters);
    setSelectedIndexFilter(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  return tableData != null ? (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <CameraTableControls
          onSearch={onSearchHandler}
          filters={selectedIndexFilter}
          baseFilter={baseFilter}
          onFilter={onFilterHandler}
          hasInsertUpdateRight={hasInsertUpdateRight}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <DeleteDialog
          scope="camera"
          isOpen={isDeleteDialogOpen}
          promise={undefined}
          promiseAll={() =>
            deleteEntities(
              cameraType?.data.id ?? "",
              Object.keys(selectedRowIds)
            )
          }
          onClose={onDeleteClose}
          ids={Object.keys(selectedRowIds)}
        />
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          loading={!camerasData && !camerasError}
        />
      </HvGrid>
    </HvGrid>
  ) : (
    <></>
  );
};
export default CameraTable;
